import { srOnly } from '../organic/motion';

/*
 * One node of the hub's pipeline canvas, n8n-style: a card with an in-port
 * and an out-port, its stage's colour down the leading edge, and a header
 * button that opens the node's table (or, for Collection, Field Mode).
 *
 * The header and the state counts are SIBLING buttons inside a
 * role="group": a button inside a button is invalid HTML and reads as one
 * control to a screen reader. A count that does not filter anything is
 * plain text, not a button that does nothing.
 *
 * A node that cannot be used here (Collection, for someone Field Mode is
 * not open to) is aria-disabled rather than disabled: it stays focusable,
 * and its tooltip still says why. `pressed` null is a header that navigates
 * rather than toggles a table, and so carries no aria-pressed.
 *
 * `ports` says which ends a wire meets: a trigger has nothing coming in, a
 * storage unit nothing going out.
 *
 * Position and size come from the canvas (inline left/top/width/minHeight);
 * on a phone index.css drops them and the nodes flow as cards.
 */
const fmt = (n) => (n === null || n === undefined ? '—' : String(n));

export default function HubNode({
  id, icon, color, title, subtitle, count, countLabel, tip, pressed = false, disabled = false,
  onOpen, states = [], note, wide = false, compact = false, ports = { in: true, out: true }, style, children,
}) {
  const titleId = `hub-node-${id}-title`;
  return (
    <div role="group" aria-labelledby={titleId} data-testid={`hub-node-${id}`}
      className={`hub-node${wide ? ' hub-node--wide' : ''}${compact ? ' hub-node--compact' : ''}${pressed ? ' hub-node--pressed' : ''}`}
      style={{ ...style, '--hub-node-color': color }}>
      {ports.in && <span className="hub-port hub-port--in" aria-hidden="true" />}
      {ports.out && <span className="hub-port hub-port--out" aria-hidden="true" />}
      <button type="button" className="hub-node__head" aria-pressed={pressed === null ? undefined : pressed}
        aria-disabled={disabled || undefined} onClick={disabled ? undefined : onOpen} title={tip}
        data-testid={`hub-node-${id}-open`}>
        <span className="hub-node__icon" aria-hidden="true">{icon}</span>
        <span className="hub-node__name">
          <span id={titleId} className="hub-node__title">{title}</span>
          {subtitle && <span className="hub-node__sub">{subtitle}</span>}
        </span>
        <span className="hub-node__count" data-testid={`hub-node-${id}-count`}>
          {countLabel && <span style={srOnly}>{countLabel}: </span>}{fmt(count)}
        </span>
      </button>
      {states.length > 0 && (
        <ul className="hub-node__states">
          {states.map((s) => (
            <li key={s.key}>
              {s.onClick ? (
                <button type="button" className="hub-state" aria-pressed={!!s.pressed} onClick={s.onClick}
                  title={s.tip} data-testid={`hub-state-${id}-${s.key}`}>
                  <span>{s.label}</span><b>{fmt(s.count)}</b>
                </button>
              ) : (
                <span className="hub-state hub-state--static" data-testid={`hub-state-${id}-${s.key}`}>
                  <span>{s.label}</span><b>{fmt(s.count)}</b>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {note && <p className="hub-node__note">{note}</p>}
      {children && <div className="hub-node__actions">{children}</div>}
    </div>
  );
}
